import {view, createViewConfig, useIsomorphicLayoutEffect} from '@sail/engine';
import {useId} from '@sail/react-aria';
import React from 'react';
import {tokens} from '../../tokens';
import {useDialogContext} from './useDialogContext';
import {DialogCloseButton} from './DialogCloseButton';
import {css} from '../../css';
import {useUiConfig} from '../../providers/UiConfigProvider';

export type DialogHeaderProps = {
  /** Content to use for the Dialog title. */
  title?: React.ReactNode;
  /** Content to use for the Dialog subtitle. */
  subtitle?: React.ReactNode;
  /** A progress indicator for a multi-step flow. */
  stepProgress?: {stepCount: number; currentStepIndex: number};
  /** Additional arbitrary content to be included after the title/subtitle rows. */
  children?: React.ReactNode;
};

export const DialogHeaderConfig = createViewConfig({
  props: {} as DialogHeaderProps,
  name: 'DialogHeader',
  flattens: true,
});

/**
 * Dialog headers are optional, and can be used to display a title and subtitle at the top of a Dialog.
 * Headers can be omitted in cases where the contents themselves are enough to uniquely identify the dialog.
 */
export const DialogHeader = DialogHeaderConfig.createView(
  ({title, subtitle, stepProgress, children, ...props}) => {
    const labelId = useId();
    const descriptionId = useId();
    const {registerLabelId, registerDescriptionId} = useDialogContext();
    const isSmallScreen = useUiConfig()?.isSmallScreen;

    useIsomorphicLayoutEffect(() => {
      if (title) {
        registerLabelId(labelId);
      }
      if (subtitle) {
        registerDescriptionId(descriptionId);
      }
    }, [title, subtitle, labelId, descriptionId]);

    return (
      <view.header
        {...props}
        uses={[
          css({
            display: 'grid',
            gridTemplate: `
              "progress progress" auto
              "title       close" auto
              "subtitle .       " auto
              "children .       " auto / 1fr auto
            `,
          }),
          css({
            padding: 'medium',
            gapX: 'medium',
            height: 'fit',
            ...(isSmallScreen ? {paddingX: tokens.space[250]} : {}),
          }),
        ]}
      >
        {stepProgress ? (
          // TODO: this is not accessible
          <view.div
            css={{
              gridArea: 'progress',
              stack: 'x',
              gap: 'small',
              marginBottom: 'small',
              alignY: 'center',
            }}
          >
            {Array.from({length: stepProgress.stepCount}, (_, i) => i).map(
              (i) => (
                <view.div
                  key={i}
                  css={{
                    backgroundColor:
                      i > stepProgress.currentStepIndex
                        ? tokens.color.neutral[100]
                        : tokens.color.brand[500],
                    height: 'xsmall',
                    borderRadius: '10px',
                  }}
                />
              ),
            )}
            <DialogCloseButton />
          </view.div>
        ) : null}
        {title ? (
          <view.h2
            id={labelId}
            css={{
              gridArea: 'title',
              margin: 'space.0',
              color: 'emphasized',
              stack: 'x',
              alignY: 'center',
              ...(isSmallScreen
                ? {font: 'heading.medium'}
                : {font: 'heading.small'}),
            }}
          >
            {title}
          </view.h2>
        ) : null}
        {subtitle ? (
          <view.p
            id={descriptionId}
            css={{
              gridArea: 'subtitle',
              margin: 'space.0',
              font: 'body.medium',
              color: 'subdued',
              marginTop: 'xxsmall',
              marginBottom: 'space.0',
            }}
          >
            {subtitle}
          </view.p>
        ) : null}

        {!stepProgress ? <DialogCloseButton /> : null}

        <view.div css={{gridArea: 'children'}}>{children}</view.div>
      </view.header>
    );
  },
);
